import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { Search, Eye } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { UserDetailDialog } from "./UserDetailDialog";

interface UserWithCounts {
  id: string;
  user_id: string;
  display_name: string | null;
  phone: string | null;
  created_at: string;
  plants_count: number;
  diagnostics_count: number;
  plan_type: string;
}

export function UsersTable() {
  const [search, setSearch] = useState("");
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);

  const { data: users, isLoading } = useQuery({
    queryKey: ["admin-users"],
    queryFn: async () => {
      // Fetch profiles with related counts
      const { data: profiles, error } = await supabase
        .from("profiles")
        .select("id, user_id, display_name, phone, created_at")
        .order("created_at", { ascending: false });

      if (error) throw error;

      // Fetch counts for each user
      const usersWithCounts: UserWithCounts[] = await Promise.all(
        (profiles || []).map(async (profile) => {
          const [plantsRes, diagnosticsRes, subscriptionRes] = await Promise.all([
            supabase
              .from("plant_identifications")
              .select("id", { count: "exact", head: true })
              .eq("user_id", profile.user_id),
            supabase
              .from("plant_diagnostics")
              .select("id", { count: "exact", head: true })
              .eq("user_id", profile.user_id),
            supabase
              .from("subscriptions")
              .select("plan_type")
              .eq("user_id", profile.user_id)
              .maybeSingle(),
          ]);

          return {
            ...profile,
            plants_count: plantsRes.count || 0,
            diagnostics_count: diagnosticsRes.count || 0,
            plan_type: subscriptionRes.data?.plan_type || "free",
          };
        })
      );

      return usersWithCounts;
    },
  });

  const filteredUsers = users?.filter((user) => {
    if (!search) return true;
    const searchLower = search.toLowerCase();
    return (
      user.display_name?.toLowerCase().includes(searchLower) ||
      user.phone?.includes(search)
    );
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Buscar usuário..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>
      </div>

      <div className="border rounded-lg">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Nome</TableHead>
              <TableHead>Telefone</TableHead>
              <TableHead className="text-center">Plantas</TableHead>
              <TableHead className="text-center">Diagnósticos</TableHead>
              <TableHead>Plano</TableHead>
              <TableHead>Cadastro</TableHead>
              <TableHead className="w-[50px]"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <TableRow key={i}>
                  <TableCell>
                    <Skeleton className="h-4 w-24" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="h-4 w-28" />
                  </TableCell>
                  <TableCell className="text-center">
                    <Skeleton className="h-4 w-8 mx-auto" />
                  </TableCell>
                  <TableCell className="text-center">
                    <Skeleton className="h-4 w-8 mx-auto" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="h-4 w-16" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="h-4 w-20" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="h-8 w-8" />
                  </TableCell>
                </TableRow>
              ))
            ) : filteredUsers?.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                  Nenhum usuário encontrado
                </TableCell>
              </TableRow>
            ) : (
              filteredUsers?.map((user) => (
                <TableRow key={user.id}>
                  <TableCell className="font-medium">
                    {user.display_name || "Sem nome"}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {user.phone || "-"}
                  </TableCell>
                  <TableCell className="text-center">{user.plants_count}</TableCell>
                  <TableCell className="text-center">
                    {user.diagnostics_count}
                  </TableCell>
                  <TableCell>
                    <span
                      className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                        user.plan_type === "premium"
                          ? "bg-primary/10 text-primary"
                          : "bg-muted text-muted-foreground"
                      }`}
                    >
                      {user.plan_type === "premium" ? "Premium" : "Free"}
                    </span>
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {format(new Date(user.created_at), "dd/MM/yyyy", {
                      locale: ptBR,
                    })}
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setSelectedUserId(user.user_id)}
                    >
                      <Eye className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <UserDetailDialog
        userId={selectedUserId}
        open={!!selectedUserId}
        onOpenChange={(open) => !open && setSelectedUserId(null)}
      />
    </div>
  );
}
